import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../apiBaseUrl";
import {
  buildFleetNotifications,
  fetchFleetNotificationInputs,
  filterNotifications,
  notificationBadgeCount,
} from "../lib/fleetNotifications";

function useMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768,
  );
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return isMobile;
}

const TABS = [
  { id: "all", label: "All" },
  { id: "vehicle", label: "Vehicle" },
  { id: "driver", label: "Driver" },
];

const SEV_STYLE = {
  critical: { bg: "#ffebee", border: "#c62828", dot: "#c62828" },
  warning: { bg: "#fff8e1", border: "#f57f17", dot: "#f57f17" },
  info: { bg: "#e3f2fd", border: "#1565c0", dot: "#1565c0" },
};

export default function Notification() {
  const nav = useNavigate();
  const isMobile = useMobile();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [allAlerts, setAllAlerts] = useState([]);
  const [tab, setTab] = useState("all");
  const [urgency, setUrgency] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError("");
      try {
        const data = await fetchFleetNotificationInputs(axios, API_BASE_URL);
        if (cancelled) return;
        setAllAlerts(buildFleetNotifications(data));
      } catch (e) {
        if (!cancelled) {
          setLoadError(e?.message || "Could not load notifications.");
          setAllAlerts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(
    () => filterNotifications(allAlerts, { tab, urgency, search }),
    [allAlerts, tab, urgency, search],
  );

  const counts = useMemo(
    () => ({
      all: allAlerts.length,
      vehicle: allAlerts.filter((n) => n.category === "vehicle").length,
      driver: allAlerts.filter((n) => n.category === "driver").length,
      urgent: notificationBadgeCount(allAlerts),
    }),
    [allAlerts],
  );

  const S = {
    page: {
      marginLeft: isMobile ? 0 : 260,
      fontFamily: "Arial, sans-serif",
      backgroundColor: "#f0f0f1",
      color: "#000",
      minHeight: "calc(100vh - 70px)",
    },
    heading: {
      backgroundColor: "#FFC107",
      color: "#000",
      padding: isMobile ? "12px 14px" : "14px 24px",
      fontSize: isMobile ? "0.95rem" : "1.05rem",
      fontWeight: "bold",
      textTransform: "uppercase",
      letterSpacing: "1px",
      margin: 0,
    },
    body: { padding: isMobile ? "14px 12px" : "20px 24px" },
    summary: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, minmax(0, 1fr))",
      gap: 10,
      marginBottom: 16,
    },
    sumCard: {
      background: "#fff",
      border: "1px solid #ddd",
      borderRadius: 6,
      padding: "12px 10px",
      textAlign: "center",
    },
    tabRow: {
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
      marginBottom: 14,
      borderBottom: "2px solid #e0e0e0",
      paddingBottom: 8,
    },
    tabBtn: (active) => ({
      padding: "8px 14px",
      border: active ? "2px solid #000" : "1.5px solid #ccc",
      background: active ? "#FFC107" : "#fff",
      fontWeight: active ? 700 : 500,
      cursor: "pointer",
      borderRadius: 4,
      fontSize: 13,
    }),
    filters: {
      display: "flex",
      gap: 10,
      flexWrap: "wrap",
      marginBottom: 16,
      alignItems: "center",
    },
    input: {
      flex: "1 1 200px",
      padding: "9px 12px",
      border: "1.5px solid #000",
      borderRadius: 4,
      fontSize: isMobile ? 16 : 13,
      boxSizing: "border-box",
    },
    select: {
      padding: "9px 10px",
      border: "1.5px solid #000",
      borderRadius: 4,
      fontSize: 13,
      background: "#fff",
      minWidth: 140,
    },
    btn: {
      padding: "9px 16px",
      background: "#1a1a1a",
      color: "#fff",
      border: "none",
      borderRadius: 4,
      fontWeight: 700,
      cursor: "pointer",
      fontSize: 13,
    },
    list: { display: "flex", flexDirection: "column", gap: 10 },
    card: (sev) => ({
      background: SEV_STYLE[sev]?.bg || "#fff",
      border: `1.5px solid ${SEV_STYLE[sev]?.border || "#ccc"}`,
      borderRadius: 6,
      padding: "12px 14px",
      cursor: "pointer",
    }),
    cardTitle: { fontWeight: 700, fontSize: 14, marginBottom: 4 },
    cardMsg: { fontSize: 13, color: "#333", lineHeight: 1.4 },
    cardMeta: { fontSize: 11, color: "#666", marginTop: 6 },
    empty: {
      textAlign: "center",
      padding: "40px 16px",
      color: "#888",
      background: "#fff",
      borderRadius: 6,
      border: "1px solid #ddd",
    },
  };

  return (
    <div style={S.page}>
      <h2 style={S.heading}>Notifications</h2>
      <div style={S.body}>
        <div style={S.summary}>
          <div style={S.sumCard}>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{counts.urgent}</div>
            <div style={{ fontSize: 11, color: "#666" }}>Urgent</div>
          </div>
          <div style={S.sumCard}>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{counts.vehicle}</div>
            <div style={{ fontSize: 11, color: "#666" }}>Vehicle</div>
          </div>
          <div style={S.sumCard}>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{counts.driver}</div>
            <div style={{ fontSize: 11, color: "#666" }}>Driver</div>
          </div>
          <div style={S.sumCard}>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{counts.all}</div>
            <div style={{ fontSize: 11, color: "#666" }}>Total</div>
          </div>
        </div>

        <div style={S.tabRow}>
          {TABS.map((t) => (
            <button key={t.id} type="button" style={S.tabBtn(tab === t.id)} onClick={() => setTab(t.id)}>
              {t.label}
              {t.id === "all" ? ` (${counts.all})` : t.id === "vehicle" ? ` (${counts.vehicle})` : ` (${counts.driver})`}
            </button>
          ))}
        </div>

        <div style={S.filters}>
          <input
            type="search"
            placeholder="Vehicle / driver / keyword…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={S.input}
          />
          <select value={urgency} onChange={(e) => setUrgency(e.target.value)} style={S.select}>
            <option value="all">All alerts</option>
            <option value="today">Due / expires today</option>
            <option value="overdue">Overdue / expired</option>
            <option value="expiring">Expiring ≤ 7 days</option>
            <option value="pending">Pending approval</option>
          </select>
          <button
            type="button"
            style={S.btn}
            onClick={() => {
              setSearch("");
              setUrgency("all");
              setTab("all");
            }}
          >
            Clear
          </button>
        </div>

        {loadError ? (
          <p style={{ color: "#c62828", fontWeight: 600 }} role="alert">
            {loadError}
          </p>
        ) : null}

        {loading ? (
          <p style={{ color: "#666" }}>Loading…</p>
        ) : filtered.length === 0 ? (
          <div style={S.empty}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🔕</div>
            <div style={{ fontWeight: 600 }}>No alerts for this filter</div>
          </div>
        ) : (
          <div style={S.list}>
            {filtered.map((n) => (
              <div
                key={n.id}
                style={S.card(n.severity)}
                role="button"
                tabIndex={0}
                onClick={() => nav(n.link)}
                onKeyDown={(e) => e.key === "Enter" && nav(n.link)}
              >
                <div style={S.cardTitle}>{n.title}</div>
                <div style={S.cardMsg}>{n.message}</div>
                <div style={S.cardMeta}>
                  {n.category === "vehicle" ? "Vehicle management" : "Driver management"}
                  {n.dateYmd ? ` · ${n.dateYmd}` : ""}
                  {n.daysLeft < 0 ? " · Overdue" : n.daysLeft === 0 ? " · Today" : n.daysLeft <= 7 ? ` · ${n.daysLeft}d` : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
